// Cache configuration
// cachedMessagesCount: Maximum number of messages to cache per chat
// cacheDays: Maximum age in days for cached messages before they are filtered out
const cachedMessagesCount = 20;
const cacheDays = 7;

const CACHE_KEY = 'chat_messages_cache';

function getCache() {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    return cache ? JSON.parse(cache) : {};
  } catch (error) {
    console.error('[messageCache] Error reading cache:', error);
    return {};
  }
}

function setCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('[messageCache] Error writing cache:', error);
  }
}

/**
 * Pull messages from cache for a specific chat
 * Updates last accessed timestamp and returns immediately
 * @param {string} chatId - The chat ID to retrieve from cache
 * @returns {Array|null} Cached messages or null if not found
 */
export function pullFromCache(chatId) {
  const cache = getCache();
  const chatCache = cache[chatId];
  
  if (!chatCache) {
    return null;
  }
  
  // Update last accessed timestamp
  chatCache.lastAccessed = Date.now();
  setCache(cache);
  
  return chatCache.messages;
}

/**
 * Save messages to cache for a specific chat
 * Filters out old cache entries and limits to cachedMessagesCount
 * @param {string} chatId - The chat ID to cache
 * @param {Array} messages - The messages to cache
 */
export function saveToCache(chatId, messages) {
  const cache = getCache();
  const now = Date.now();
  const maxAge = cacheDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds
  
  // Filter out cache entries older than cacheDays
  Object.keys(cache).forEach(key => {
    if (cache[key].lastAccessed && (now - cache[key].lastAccessed) > maxAge) {
      delete cache[key];
    }
  });
  
  // Save messages for this chat, limited to cachedMessagesCount
  cache[chatId] = {
    messages: messages.slice(-cachedMessagesCount), // Keep only last N messages
    lastAccessed: now,
  };
  
  setCache(cache);
}

/**
 * Update cache with new messages for a specific chat
 * Merges new messages with existing cache, updates last accessed, and limits to cachedMessagesCount
 * @param {string} chatId - The chat ID to update
 * @param {Array} newMessages - The new messages to add
 */
export function updateCache(chatId, newMessages) {
  const cache = getCache();
  const chatCache = cache[chatId] || { messages: [], lastAccessed: Date.now() };
  
  // Merge messages, avoiding duplicates by ID
  const existingIds = new Set(chatCache.messages.map(m => m.id));
  const mergedMessages = [
    ...chatCache.messages,
    ...newMessages.filter(m => !existingIds.has(m.id))
  ];
  
  // Update cache with merged messages, limited to cachedMessagesCount
  cache[chatId] = {
    messages: mergedMessages.slice(-cachedMessagesCount),
    lastAccessed: Date.now(),
  };
  
  setCache(cache);
}
